import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import {
  Box,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import React, { useEffect, useState } from "react";
import { SearchSection } from "../components/SearchSection";
import type {
  DataGridRow,
  StoredPicturesDownloadCondition,
  SearchCondition,
} from "../types";
import { StoredPicturesFeedGrid } from "../components/StoredPicturesFeedGrid";
import { getStoredPicturesListAPI } from "../api";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { GetPrefecturesAPI } from "@/api";
import { downloadStoredPicturesAPI } from "@/api/downloadStoredPicturesAPI";
import { type IPrefecture } from "@/types/prefectures";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { useNavigate, useSearchParams } from "react-router-dom";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import { toast } from "react-toastify";
import { LakersBackendApiRequestTimeoutError } from "@/utils/errors";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { formatNumberWithComma } from "@/utils/formatNumber";

const MAX_DOWNLOAD_COUNT = 100;

export const StoredPictures: React.FC = () => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();
  const [searchParams] = useSearchParams();
  const initialOwnerName = searchParams.get("ownerName") ?? undefined;

  // ユーザコンフィグを取得するフック
  const userConfig = UserConfigGetAPI();

  // 結果表示部分に表示する行のState
  const [rows, setRows] = useState<DataGridRow[]>([]);

  // ローディングの状態を管理するState
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ダウンロード内容のState
  const defaultDownloadCondition: StoredPicturesDownloadCondition = {
    billableData: [],
    noBillableData: [],
  };
  const [downloadCondition, setDownloadCondition] =
    useState<StoredPicturesDownloadCondition>(defaultDownloadCondition);

  // ダウンロード確認モーダル表示のState
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 都道府県リストのState
  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);

  // 選択された行のIDを管理するState
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);

  // 検索項目のState
  const [searchCondition, setSearchCondition] = useState<SearchCondition>();

  // 一括ダウンロード時にリユース優先でするか、取得済優先でダウンロードするかのラジオを表示するかのState
  const [showReusePriorityDownloadOption, setShowReusePriorityDownloadOption] =
    useState<boolean>(false);

  // リユース優先で一括ダウンロードするかどうかのState
  const [isReusePriorityDownload, setIsReusePriorityDownload] =
    useState<boolean>(true);

  useEffect(() => {
    (async () => {
      // 都道府県リストをAPIから取得する。
      const prefectures = await GetPrefecturesAPI(apiClient);
      setPrefectures(prefectures);
    })();
  }, []);

  const handleSearch = (searchCondition: SearchCondition): void => {
    (async () => {
      setIsLoading(true);
      try {
        const rows = await getStoredPicturesListAPI(apiClient, searchCondition);
        setSearchCondition(searchCondition);
        setRows(rows);
      } catch (error) {
        if (error instanceof LakersBackendApiRequestTimeoutError) {
          toast.info(error.message);
          return;
        }

        toast.error(
          "検索に失敗しました。しばらく経ってから再度お試しください。"
        );
      } finally {
        setIsLoading(false);
      }
    })();
  };

  /**
   * ダウンロード条件を計算する共通関数
   *
   * NOTE: 課金額の計算について
   * リユース優先で一括ダウンロードする場合は、
   * 取得済登記を保持していない、または取得済登記がリユース登記より古い場合に料金が発生する。
   * 取得済優先で一括ダウンロードする場合は、
   * 取得済登記を保持していない場合は課金する。
   * それ以外の選択した登記は料金が発生しない。
   * @param {boolean} isReusePriority - リユース優先かどうか
   * @returns {object} newDownloadCondition - 新しいダウンロード条件, shouldShowOption - オプション表示フラグ
   */
  const calculateDownloadCondition = (
    isReusePriority: boolean
  ): {
    newDownloadCondition: StoredPicturesDownloadCondition;
    shouldShowOption: boolean;
  } => {
    const newDownloadCondition: StoredPicturesDownloadCondition = {
      billableData: [],
      noBillableData: [],
    };
    let shouldShowOption = false;

    selectedRowIds.forEach((value) => {
      const row = rows.find((rowElement) => rowElement.id === value);
      if (row != null) {
        // リユース優先ダウンロードオプションを表示するかどうかを判定
        if (
          row.lastUserAcquisitionDate &&
          row.lastUserAcquisitionDate < row.receivedDate
        ) {
          shouldShowOption = true;
        }

        if (isReusePriority) {
          // リユース優先で一括ダウンロードする場合の料金が発生する条件
          // 取得済登記を保持していない、または取得済登記がリユース登記より古い場合に課金する
          if (
            row.lastUserAcquisitionDate === null ||
            row.lastUserAcquisitionDate < row.receivedDate
          ) {
            newDownloadCondition.billableData.push({
              id: row.id,
              price: row.chargeAmount,
              receivedId: row.receivedId,
            });
          } else {
            newDownloadCondition.noBillableData.push({
              id: row.id,
            });
          }
        } else {
          // 取得済優先で一括ダウンロードする場合の料金が発生する条件
          // 取得済登記を保持していない場合は課金する
          if (row.lastUserAcquisitionDate === null) {
            newDownloadCondition.billableData.push({
              id: row.id,
              price: row.chargeAmount,
              receivedId: row.receivedId,
            });
          } else {
            newDownloadCondition.noBillableData.push({
              id: row.id,
            });
          }
        }
      }
    });

    return { newDownloadCondition, shouldShowOption };
  };

  /**
   * ダウンロードボタンを押したときにIDと金額を計算する関数
   */
  const handleModalOpen = (): void => {
    // ラジオボタンの初期値をリユース優先にリセット
    setIsReusePriorityDownload(true);

    const { newDownloadCondition, shouldShowOption } =
      calculateDownloadCondition(true);

    setDownloadCondition(newDownloadCondition);
    setShowReusePriorityDownloadOption(shouldShowOption);
    setIsOpen(true);
  };

  // ダウンロード対象に注記（最新の登記では所有者が異なる可能性）のある行が含まれるか。
  // 単票ダウンロード・一括ダウンロードのどちらでもモーダル表示時に判定できるよう、
  // ダウンロード条件（課金対象＋課金対象外）のIDから導出する
  const downloadTargetIds = [
    ...downloadCondition.billableData.map((data) => data.id),
    ...downloadCondition.noBillableData.map((data) => data.id),
  ];
  const includesLatestOwnerAnnotatedRow = rows.some(
    (row) => downloadTargetIds.includes(row.id) && row.isLatestOwner === false
  );

  /**
   * 課金対象データの金額を合算する。
   * @returns {string} 合計金額
   */
  const sumChargeAmount = (): string => {
    const price = downloadCondition.billableData.reduce((a, x) => {
      return a + x.price;
    }, 0);
    return `${price.toLocaleString()}円`;
  };

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <FindInPageIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>不動産オーナーから不動産登記を検索する</PageTitle>
      </Box>

      {/* 検索部分 */}
      <SectionPaper>
        <SearchSection
          handleSearch={handleSearch}
          prefectures={prefectures}
          isLoading={isLoading}
          initialOwnerName={initialOwnerName}
          key={initialOwnerName}
        />
      </SectionPaper>

      {/* 余白 */}
      <Box sx={{ mt: 3 }} />

      {/* ダウンロードボタン */}
      <Box
        sx={{
          my: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          label="一括ダウンロード"
          disabled={selectedRowIds.length === 0}
          onClick={handleModalOpen}
          variant={ButtonVariantOption.Outlined}
        />
      </Box>

      {/* 最大ダウンロード件数文言表示 */}
      <Box
        sx={{
          my: 1,
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <Typography variant="body1">
          最大{formatNumberWithComma(MAX_DOWNLOAD_COUNT)}件まで選択できます。
        </Typography>
      </Box>

      {/* 結果表示部分 */}
      <SectionPaper>
        <StoredPicturesFeedGrid
          rows={rows}
          setIsOpen={setIsOpen}
          setDownloadCondition={setDownloadCondition}
          apiClient={apiClient}
          setSelectedRowIds={setSelectedRowIds}
          navigate={navigate}
          maxDownloadCount={MAX_DOWNLOAD_COUNT}
          loading={isLoading}
          isReadOnly={isReadOnly}
        />
      </SectionPaper>

      {/* 課金確認モーダル */}
      <CustomModal
        isOpen={isOpen}
        handleClose={(_event, reason) => {
          // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setIsOpen(false);
        }}
        ariaLabelledby="download-modal"
        ariaDescribedby="download-modal-description"
      >
        <PageTitle>課金確認</PageTitle>
        <Box>
          {/* 注記のある行が含まれる場合は、ダウンロード前に注記を再度表示する */}
          {includesLatestOwnerAnnotatedRow && (
            <Typography sx={{ my: 1, color: "warning.main", fontWeight: 600 }}>
              最新の登記では所有者が異なる可能性がある登記が含まれています。
            </Typography>
          )}

          {showReusePriorityDownloadOption && (
            <FormControl sx={{ my: 1 }}>
              <RadioGroup
                row
                value={isReusePriorityDownload ? "reuse" : "used"}
                onChange={(e) => {
                  const newIsReusePriority = e.target.value === "reuse";
                  setIsReusePriorityDownload(newIsReusePriority);
                  const { newDownloadCondition } =
                    calculateDownloadCondition(newIsReusePriority);
                  setDownloadCondition(newDownloadCondition);
                }}
              >
                <FormControlLabel
                  value="reuse"
                  control={<Radio />}
                  label="リユース登記優先で一括ダウンロードする"
                />
                <FormControlLabel
                  value="used"
                  control={<Radio />}
                  label="取得済登記優先で一括ダウンロードする"
                />
              </RadioGroup>
            </FormControl>
          )}

          {userConfig === undefined ? null : userConfig.isPriceHidden ? (
            <>
              <Typography>課金が発生します。</Typography>
              <Typography>よろしいですか？</Typography>
            </>
          ) : (
            <>
              <Typography>{sumChargeAmount()}課金されます。</Typography>
              <Typography>よろしいですか？</Typography>

              {/* 課金対象、課金対象外件数・金額を表示 */}
              <Box
                sx={{
                  my: 3,
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead sx={{ bgcolor: "whitesmoke" }}>
                      <TableRow>
                        <TableCell>名前</TableCell>
                        <TableCell>件数</TableCell>
                        <TableCell>金額</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>課金対象</TableCell>
                        <TableCell>
                          {formatNumberWithComma(
                            downloadCondition.billableData.length
                          )}
                        </TableCell>
                        <TableCell>{sumChargeAmount()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>課金対象外</TableCell>
                        <TableCell>
                          {formatNumberWithComma(
                            downloadCondition.noBillableData.length
                          )}
                        </TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              onClick={() => {
                setDownloadCondition(defaultDownloadCondition);
                setIsOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <SubmitButton
                label="はい"
                disabled={isReadOnly}
                onClick={(_event, cancelLoading) => {
                  void (async () => {
                    try {
                      await downloadStoredPicturesAPI(
                        apiClient,
                        downloadCondition,
                        navigate
                      );
                      if (searchCondition) {
                        const response = await getStoredPicturesListAPI(
                          apiClient,
                          searchCondition
                        );
                        setRows(response);
                      }
                    } finally {
                      setDownloadCondition(defaultDownloadCondition);
                      setIsOpen(false);
                      cancelLoading();
                    }
                  })();
                }}
                timeout={30000}
                variant={ButtonVariantOption.Contained}
              />
            </ReadOnlyTooltip>
          </Box>
        </Box>
      </CustomModal>
    </PagePaper>
  );
};
